import type { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import * as S from './MyAdsBulkPromotion.styles';
import { AD_KIND_BY_SERVICE_TYPE } from './MyAdsBulkPromotion.constants';
import * as AdvertiseS from '@/components/Ad/Promotions/Advertise.styles';
import { promotions } from '@/components/Ad/Promotions/Promotions.constants';
import * as PromotionsS from '@/components/Ad/Promotions/Promotions.styles';
import * as PromotionS from '@/components/Ad/Promotions/Promotion.styles';
import type { BulkPromotionService } from '@/features/Profile/sections/MyAds/api';
import { Modal } from '@/components/UI/Modal';
import { BottomSheet } from '@/components/UI/BottomSheet/BottomSheet';
import { Button } from '@/components/UI/Button/Button';
import { AngleIcon, CloseIcon } from '@/components/UI/Icons/Icons';
import { IconBox } from '@/components/UI/IconBox/IconBox.styles';
import { SkeletonLoader } from '@/components/SkeletonLoader/SkeletonLoader';
import { useResponsive } from '@/hooks/useResponsive';
import { AdKind } from '@/api/types/common.types';

type Props = {
  selectedCount: number;
  services: BulkPromotionService[];
  isOpen: boolean;
  isLoading: boolean;
  onClose: () => void;
  onSelect: (service: BulkPromotionService) => void;
};

export function MyAdsBulkPromotionServicePicker({
  selectedCount,
  services,
  isOpen,
  isLoading,
  onClose,
  onSelect,
}: Props) {
  const { t } = useTranslation();
  const theme = useTheme();
  const { isDesktop } = useResponsive();
  const promotionPresentations = promotions(t, 0, 0, 0, true);

  function handleServiceClick(
    event: MouseEvent<HTMLAnchorElement>,
    service: BulkPromotionService
  ) {
    event.preventDefault();
    onSelect(service);
  }

  const content = (
    <AdvertiseS.ModalWrapper $isBottomSheet={!isDesktop}>
      <AdvertiseS.ModalHeader $isBottomSheet={!isDesktop}>
        <AdvertiseS.ModalTitle>
          {t('pages.ad.select_service')}
        </AdvertiseS.ModalTitle>
        <Button appearance="transparent" hasPadding={false} onClick={onClose}>
          <CloseIcon
            width={isDesktop ? 20 : 15}
            height={isDesktop ? 20 : 15}
            fill={theme.palette.secondary.labels}
          />
        </Button>
      </AdvertiseS.ModalHeader>
      <S.ServicePickerDescription>
        {t('pages.cabinet.bulk_promotion_apply_description', {
          count: selectedCount,
        })}
      </S.ServicePickerDescription>
      <PromotionsS.Box>
        {isLoading &&
          Array.from({ length: 3 }, (_, index) => (
            <SkeletonLoader key={index} borderRadius={14} height={64} />
          ))}

        {!isLoading &&
          services.map(service => {
            const adKind = AD_KIND_BY_SERVICE_TYPE[service.serviceType];
            const presentation = promotionPresentations.find(
              promotion => promotion.type === adKind
            );
            const boxLabel = presentation?.boxLabel;

            return (
              <PromotionS.Box
                key={service.serviceType}
                href="#"
                $appearance="secondary"
                onClick={event => handleServiceClick(event, service)}
              >
                <PromotionS.Texts $isBoxLabelPresented={Boolean(boxLabel)}>
                  <PromotionS.TitleBox>
                    <PromotionS.Title>
                      {presentation?.title ?? service.title}
                    </PromotionS.Title>
                    {presentation?.icon && (
                      <PromotionS.Icons>
                        <IconBox
                          $filename={presentation.icon}
                          $width={12}
                          $height={10}
                          $iconWidth={12}
                          $iconHeight={10}
                        />
                      </PromotionS.Icons>
                    )}
                  </PromotionS.TitleBox>
                  {presentation?.description && (
                    <PromotionS.Description>
                      {presentation.description}
                    </PromotionS.Description>
                  )}
                </PromotionS.Texts>
                <S.ServiceArrow>
                  <AngleIcon />
                </S.ServiceArrow>
                {boxLabel && (
                  <PromotionS.BoxLabel
                    $isVipBg={presentation?.type === AdKind.Vipped}
                    $isPremiumBg={presentation?.type === AdKind.Premium}
                  >{`+ ${boxLabel}`}</PromotionS.BoxLabel>
                )}
              </PromotionS.Box>
            );
          })}
      </PromotionsS.Box>
    </AdvertiseS.ModalWrapper>
  );

  if (isDesktop) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} maxWidth={600}>
        {content}
      </Modal>
    );
  }

  return (
    <BottomSheet isOpen={isOpen} close={onClose}>
      {content}
    </BottomSheet>
  );
}
